import {
  config
} from 'aws-sdk'

import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession
} from 'amazon-cognito-identity-js'

import store from '../store'
import { tmxApiCall } from './index'
import paymentMethod from './paymentMethod'

// Utilities
import isEmpty from '../utility/isEmpty'
import Logger from '../utility/loggerUtil'
import Session from '../utility/sessionUtil'

export default {
  /**
   * Authenticates the passed in info.
   * @param {String} username - Email address/phone number of the user.
   * @param {String} password - The password for the user.
   * @param {Boolean} fromLegacyNcr - Flag to indicate whether the customer was transferred from Legacy NCR Site.
   * @returns {Promise<{accessToken: String, expiresIn: Number, idToken: String, refreshToken: String, tokenType: String, deviceKey: String, deviceGroupKey: String, serviceConfig: {clientId: String, cognitoUrl: String, userPoolId: String, region: String}}>} The authenticated tokens and Cognito data.
   */
  async authenticate (username, password, fromLegacyNcr) {
    Logger.info('Started authentication')
    const data = await tmxApiCall('post', '/authenticate', {
      username,
      password,
      encodedData: getEncodedData(username),
      fromLegacyNcr
    })

    await store.dispatch('session/setDeviceKey', data.deviceKey)

    return data
  },

  /**
   * Gets new auth tokens from an existing refresh token.
   * @param {String} profileId - The cognito profile identifier.
   * @param {String} deviceGroupKey - The device group key provided during auth.
   * @param {String} refreshToken - The valid refresh JWT token.
   * @returns {Promise<{accessToken: String, expiresIn: Number, idToken: String, refreshToken: String, tokenType: String, deviceKey: String, deviceGroupKey: String, serviceConfig: {clientId: String, cognitoUrl: String, userPoolId: String, region: String}}>} The authenticated tokens and Cognito data.
   */
  async refreshAuth (profileId, deviceGroupKey, refreshToken) {
    Logger.info('Started refresh auth')
    const data = await tmxApiCall('put', '/authenticate', {
      profileId,
      deviceGroupKey,
      encodedData: getEncodedData(profileId),
      refreshToken
    })

    await store.dispatch('session/setDeviceKey', data.deviceKey)

    return data
  },

  /**
   * Logs a user into the application.
   * @param {String} username - Email address or Phone Number of the user to be logged in.
   * @param {String} password - Password of the user to be logged in.
   * @param {Boolean} rememberMe - True if the user should be remembered.
   * @param {Boolean} fromLegacyNcr - True if the user was transferred from Legacy NCR Site.
   * @returns {Promise<{user: String, serviceConfig: {clientId: String, cognitoUrl: String, userPoolId: String}}>} User guid, the cognito identifier and cognito connection details.
   */
  async login (username, password, rememberMe, fromLegacyNcr) {
    Logger.info('Started the Process!')

    let authResult

    try {
      authResult = await this.authenticate(username, password, fromLegacyNcr)
    } catch (e) {
      if (e.response && e.response.data && e.response.data.errorCode) { // If we get an error code, throw new error using code
        if (e.response.data.errorCode.includes('UserNotConfirmedException')) {
          Logger.error('User not verified')
          store.commit('session/unverified', { user: undefined, username })
        }

        Logger.error('Authentication failed')
        Logger.debug(e.response.data.errorCode)
        throw new Error(e.response.data.errorCode)
      }

      throw e
    }

    return await this.processAuthResult(authResult, username, rememberMe)
  },

  async loginFromAuthResult (authResult, username) {
    return await this.processAuthResult(authResult, username, false)
  },

  async processAuthResult (authResult, username, rememberMe) {
    try {
      const { accessToken, deviceKey, deviceGroupKey, idToken, refreshToken, serviceConfig } = authResult

      Session.clearIdentitySessions()

      config.update({
        region: serviceConfig.region
      })

      Logger.debug('Step 1')

      const cognitoUserPool = getUserPool(serviceConfig)
      const AccessToken = new CognitoAccessToken({ AccessToken: accessToken })
      const IdToken = new CognitoIdToken({ IdToken: idToken })
      const RefreshToken = new CognitoRefreshToken({ RefreshToken: refreshToken })

      const sessionData = {
        IdToken,
        AccessToken,
        RefreshToken
      }

      const userSession = new CognitoUserSession(sessionData)

      Logger.debug('Step 2')

      const userData = {
        Username: username,
        Pool: cognitoUserPool,
        Storage: window.sessionStorage
      }

      const cognitoUser = new CognitoUser(userData)

      cognitoUser.setSignInUserSession(userSession)
      cognitoUser.setAuthenticationFlowType('USER_PASSWORD_AUTH') // Needed to authenticate without a hashed password

      // The SDK only persists LastAuthUser/tokens via cacheTokens(). Without it, refreshSession() can't find the cached device key and fails with NotAuthorizedException.
      cognitoUser.cacheTokens()

      // This seems to be the only way to fill in these properties when not using the client SDK authenticate method
      cognitoUser.deviceKey = deviceKey
      cognitoUser.deviceGroupKey = deviceGroupKey

      cognitoUser.cacheDeviceKeyAndPassword()
      cognitoUser.getCachedDeviceKeyAndPassword()

      const user = await new Promise((resolve, reject) => {
        cognitoUser.getSession((err, session) => { // You must run this to verify that session (internally)
          if (err) {
            Logger.error('Error on get session')
            Logger.debug(err)
            reject(err)
            return
          }

          if (session.isValid()) {
            Logger.debug(`access token: ${session.getAccessToken().getJwtToken()}`)

            resolve(cognitoUser)
          } else {
            Logger.error('Invalid session')
            Logger.debug(session)
            reject(new Error('Invalid session')) // TODO: Handle this error appropriately
          }
        })
      })

      Logger.debug('Step 3')

      if (rememberMe) {
        user.setDeviceStatusRemembered({
          onSuccess (result) {
            Logger.info('Remember Me call result')
            Logger.debug(JSON.stringify(result))
          },
          onFailure (err) {
            Logger.error('Remember Me call error')
            Logger.debug(err)
          }
        })
      } else {
        user.setDeviceStatusNotRemembered({
          onSuccess (result) {
            Logger.info('Do Not Remember Me call result')
            Logger.debug(JSON.stringify(result))
          },
          onFailure (err) {
            Logger.error('Do Not Remember Me call error')
            Logger.debug(err)
          }
        })
      }

      Logger.debug('Step 4')

      if (store.state.payment.guestCard && store.state.payment.saveGuestCard) {
        const guestCardIds = store.state.payment.paymentCardIds
        await paymentMethod.removeDebitCard(guestCardIds).catch(e => {
          Logger.error('Error deleting guest cards.')
          Logger.debug(e)
        })
      }

      return {
        user
      }
    } catch (e) {
      Logger.error('Error attempting to log in')
      Logger.debug(e)

      throw e
    }
  },

  /**
   * Logs in the user using device key authentication endpoint.
   * @param {String} deepLinkFlow - The deep link flow from the deep link URL.
   * @param {String} profileId - The cognito profile identifier from the deep link URL.
   * @returns {Promise<{accessToken: string, expiresIn: number, idToken: string, refreshToken: string, tokenType: string, deviceKey: string, deviceGroupKey: string, serviceConfig: {clientId: string, cognitoUrl: string, userPoolId: string, region: string}}>} The authenticated tokens and Cognito data.
   */
  async deviceKeyAuth (deepLinkFlow, profileId) {
    Logger.info('Started device key auth')
    const data = await tmxApiCall('post', '/deviceKeyAuth', {
      profileId,
      deepLinkFlow
    })

    await store.dispatch('session/setDeviceKey', data.deviceKey)

    return data
  },

  /**
   * Logs in the user using signature authentication endpoint.
   * @param {String} profileId - The cognito profile identifier from the deep link URL.
   * @param {String} signature - The signature for the signed URL used to authenticate.
   * @param {String} expiration - The expiration for the signed URL used to authenticate.
   * @param {String} path - The path the user is attempting to access.
   * @param {Boolean} isGuestPayment - Whether this is a guest payment or not.
   * @param {String} [applicationId] - Optionally, the application ID if included in the deep link.
   * @returns {Promise<{accessToken: string, expiresIn: number, idToken: string, refreshToken: string, tokenType: string, deviceKey: string, deviceGroupKey: string, serviceConfig: {clientId: string, cognitoUrl: string, userPoolId: string, region: string}}>} The authenticated tokens and Cognito data.
   */
  async signatureAuth (profileId, signature, expiration, path, isGuestPayment, applicationId = undefined) {
    Logger.info('Started signature auth')
    const data = await tmxApiCall('post', '/signatureAuth', {
      profileId,
      applicationId,
      signature,
      expiration,
      path,
      isGuestPayment,
      encodedData: getEncodedData(profileId)
    })

    await store.dispatch('session/setDeviceKey', data.deviceKey)

    return data
  },

  async register (username, password, guestCardIds = undefined, lastFourOfSsn, referralId, dateOfBirth, isReferralOnly) {
    return tmxApiCall('post', '/registerUser', {
      username,
      password,
      encodedData: getEncodedData(username),
      guestCardIds,
      lastFourOfSsn,
      referralId,
      dateOfBirth,
      isReferralOnly
    })
  },

  async updateUsername (oldUsername, newUsername, password) {
    return tmxApiCall('put', '/registerUser', {
      oldUsername,
      newUsername,
      password,
      encodedData: getEncodedData(newUsername)
    })
  },

  /**
   * Verifies the user's email or phone number with a cognito code.
   * @param {String} username - The user's username (guid or phone/email).
   * @param {String} code - The verification code from cognito.
   * @param {Boolean} isEmail - True if the username is an email.
   */
  async verify (username, code, isEmail = false) {
    Logger.info('Started post verify user call.')
    return tmxApiCall('post', '/confirmuser', {
      code,
      encodedData: getEncodedData(username),
      isEmail,
      username
    })
  },

  /**
   * After verification is successful, set status to success.
   * @param {String} username - The user's username (guid or phone/email).
   * @param {Boolean} [isVoice] - True if calling user with code instead of text message.
   */
  async verifyConfirmation (username, isVoice = false) {
    Logger.info('Started post verify confirm user call.')
    return tmxApiCall('put', '/confirmuser', {
      username,
      isVoice
    })
  },

  /**
   * Signs out the current device key and clears the remote locked flow.
   */
  async deviceKeySignout () {
    return tmxApiCall('put', '/deviceKeyAuth')
  },

  async signout () {
    const userPool = getUserPool()
    const cognitoUser = userPool.getCurrentUser()

    if (cognitoUser != null) {
      await new Promise((resolve) => {
        cognitoUser.signOut(() => {
          try {
            cognitoUser.clearCachedDeviceKeyAndPassword() // This appears to force new device key to be generated, without this we get a device not found error
          } catch (e) {
            Logger.error('Failed to get set device info')
            Logger.debug(e)
          }

          resolve()
        })
      })
    }
  },

  async resendVerificationCode (profileId, username, isVoice) {
    Logger.info('Started resend verification code call.')
    return tmxApiCall('post', '/resendVerificationCode', {
      profileId,
      username,
      isVoice,
      encodedData: getEncodedData(profileId)
    })
  },

  async getUser () {
    const userPool = getUserPool()

    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser()

      if (cognitoUser == null) {
        reject(new Error('UserNotFound'))
        return
      }

      cognitoUser.getSession((err, result) => {
        if (err) {
          reject(err)
        } else if (result.isValid()) {
          resolve({ user: cognitoUser, username: cognitoUser.getUsername(), accessToken: result.getAccessToken().getJwtToken() })
        }
      })
    })
  },

  async getAttributes () {
    const userPool = getUserPool()

    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser()

      if (cognitoUser == null) {
        reject(new Error('UserNotFound'))
        return
      }

      cognitoUser.getSession(async (err, session) => {
        if (err) {
          Logger.error('Error getting session while getting user attributes.')
          Logger.debug(err)
          reject(err)
          return
        }

        Logger.debug('Got user session access token')
        Logger.trace(session.getAccessToken())

        if (!session.isValid() || session.accessToken.getExpiration() < Math.round((new Date()).getTime() / 1000)) {
          Logger.info('Token is not valid, attempting to refresh...')
          try {
            await this.refreshToken()
          } catch (e) {
            Logger.error('Error refreshing token while getting user attributes.')
            Logger.debug(e)
            reject(e)
            return
          }
        }

        cognitoUser.getUserAttributes((err, result) => {
          Logger.debug('Get attribute result: ')
          Logger.debug(JSON.stringify(result))

          if (err) {
            Logger.error('Error getting user attributes')
            Logger.debug(err)
            reject(err)
          } else {
            resolve(result)
          }
        })
      })
    })
  },

  async updateAttribute (attribute) {
    const userPool = getUserPool()

    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser()

      if (cognitoUser == null) {
        reject(new Error('UserNotFound'))
        return
      }

      cognitoUser.getSession((err, result) => {
        if (err) {
          Logger.error('Error getting session')
          Logger.debug(err)
          reject(err)
          return
        }

        const attributeList = []
        const attr = new CognitoUserAttribute(attribute)

        attributeList.push(attr)

        cognitoUser.updateAttributes(attributeList, (err, result) => {
          Logger.debug('Update attribute result')

          if (err) {
            Logger.error('Error updating attributes')
            Logger.debug(err)
            reject(err)
          } else {
            Logger.debug(JSON.stringify(result))
            resolve(result)
          }
        })
      })
    })
  },

  async checkTokenExpiration () {
    Logger.info('Checking token expiration...')

    const userPool = getUserPool()
    const cognitoUser = userPool.getCurrentUser()

    return new Promise((resolve, reject) => {
      if (cognitoUser == null) {
        Logger.error('No user found when getting session')
        reject(new Error('UserNotFound'))
        return
      }

      cognitoUser.getSession((err, session) => {
        Logger.debug('Got session')

        if (err) {
          Logger.error('Error getting session')
          Logger.debug(err)
          reject(err)
          return
        }

        Logger.debug('Session validity: ' + session.isValid())
        resolve(session.isValid())
      })
    })
  },

  async refreshToken (forceRefresh = false) {
    Logger.info('Refreshing token expiration...')

    const userPool = getUserPool()
    const cognitoUser = userPool.getCurrentUser()

    return new Promise(async (resolve, reject) => {
      if (cognitoUser == null) {
        Logger.error('Error getting Cognito user, not found')
        reject(new Error('UserNotFound'))
        return
      }

      cognitoUser.getSession(async (err, getSession) => {
        if (err) {
          Logger.error('Error getting Cognito session')
          Logger.debug(err)
          reject(err)
          return
        }

        Logger.debug(`Session validity: ${getSession.isValid()}`)

        if (!forceRefresh && (getSession.isValid() || getSession.accessToken.getExpiration() > Math.round((new Date()).getTime() / 1000))) { // No need to refresh if already valid
          resolve({ user: cognitoUser })
          return
        }

        const profileId = store.getters['session/getCognitoId']
        const refreshToken = getSession.getRefreshToken().getToken()
        const username = cognitoUser.getUsername()

        cognitoUser.getCachedDeviceKeyAndPassword() // Populate deviceKey/deviceGroupKey from storage - this is the device key actually tied to this session

        Logger.debug(`Refresh token: ${JSON.stringify(refreshToken)}`)

        try {
          const authResult = await this.refreshAuth(profileId, cognitoUser.deviceGroupKey, refreshToken)

          const { user } = await this.processAuthResult(authResult, username, true) // We always remember the device today, so keep that status intact through a refresh

          config.credentials.Logins = {}
          config.credentials.Logins[store.state.session.servicesConfig.cognitoUrl + '/' + store.state.session.servicesConfig.userPoolId] = authResult.idToken

          config.credentials.refresh((error) => {
            if (error) {
              Logger.error('Error refresh AWS session')
              Logger.debug(error)
              reject(error)
              return
            }

            Logger.debug(`Successfully logged! JWT is: ${authResult.idToken}`)
            resolve({ accessToken: authResult.idToken, user })
          })
        } catch (e) {
          Logger.error('Error refreshing Cognito session')
          Logger.debug(e)
          reject(e)
        }
      })
    })
  },

  async sendPasswordReset (username, isVoice = false) {
    Logger.info('Started password reset request.')

    return tmxApiCall('post', '/resetpassword', {
      username,
      isVoice
    })
  },

  async confirmPasswordReset (username, code, password) {
    Logger.info('Started confirm password reset request.')

    return tmxApiCall('post', '/confirmpassword', {
      username,
      verificationCode: code,
      newPassword: password,
      encodedData: getEncodedData(username)
    })
  },

  async confirmPasswordResetCode (username, code) {
    Logger.info('Started confirm code request.')

    return tmxApiCall('put', '/confirmpassword', {
      username,
      verificationCode: code
    })
  },

  async getCurrentDevice () {
    Logger.info('Started get current device query.')

    const userPool = getUserPool()
    const cognitoUser = userPool.getCurrentUser()

    if (cognitoUser == null) {
      throw new Error('UserNotFound')
    }

    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err, getSession) => {
        if (err) {
          Logger.error('Error getting Cognito session before device query')
          Logger.debug(err)
          reject(err)
          return
        }

        cognitoUser.getCachedDeviceKeyAndPassword() // The device key is stored is session storage with other user info, this pulls from storage and inserts into user object

        if (isEmpty(cognitoUser.deviceKey)) {
          Logger.error('Device key is missing')
          throw new Error('Device key not found')
        }

        cognitoUser.getDevice({
          onSuccess: result => {
            Logger.info('Got current device')
            Logger.debug(JSON.stringify(result))
            resolve(result)
          },
          onFailure: err => {
            Logger.error('Error getting current device')
            Logger.debug(err)
            reject(err)
          }
        })
      })
    })
  },

  /**
   * Checks to see if the passed in device key is associated with a valid trusted device record or not.
   * @param {String} deviceKey - The unique identifier for an authenticated user's device (currently generated by Cognito).
   * @param {String} isEmail - True if the username to verify is an email address.
   */
  async getTrustedDeviceStatus (deviceKey, isEmail) {
    Logger.info('Started get trusted device status query')

    return tmxApiCall('get', '/checktrusteddevice', {
      params: {
        deviceKey,
        isEmail
      }
    })
  },

  /**
   * Sends a new code to the Cognito account in order to confirm a device using the passed device key.
   * @param {String} deviceKey - The unique identifier for an authenticated user's device (currently generated by Cognito).
   * @param {String} isEmail - True if the username to verify is an email address.
   * @param {Boolean} isVoice - True if calling user with code instead of text message.
   */
  async sendTrustedDeviceCode (deviceKey, isEmail, isVoice) {
    Logger.info('Started send trusted device code query')

    return tmxApiCall('get', '/sendtrusteddevice', {
      params: {
        deviceKey,
        isEmail,
        isVoice
      }
    })
  },

  /**
   * Trusts the device using the passed in verification code.
   * @param {String} deviceKey - The unique identifier for an authenticated user's device (currently generated by Cognito).
   * @param {String} code - The unique confirmation code sent to the user to be confirmed.
   * @param {String} profileId - The user identifier for the user the initiated the trusted device confirmation.
   */
  async confirmTrustedDeviceCode (deviceKey, code, profileId) {
    return tmxApiCall('post', '/confirmtrusteddevice', {
      deviceKey,
      verificationCode: code,
      profileId
    })
  },

  /**
   * Checks the status of the user update.
   * @param {String} newAlias - The new phone or email being verified.
   */
  async getUpdateUserStatus (newAlias) {
    Logger.info('Started Get Update User Status call')
    return tmxApiCall('get', '/updateUser', {
      params: {
        newAlias
      }
    })
  },

  /**
   * Updates the users contact information
   * @param {String} newAlias - The new phone or email.
   * @param {Boolean} isVoice - Send code by phone call instead of text message.
   * @param {Object} [communicationSettings] - The communication settings the user is opting into
   * @param {Boolean} [isEmailPrimary] - Is the email the primary contact?
   * @returns {Promise<{verificationSent: Boolean}>} Whether or not verification was sent. If false, the feature is disabled.
   */
  async updateUserContactInfo (newAlias, isVoice, communicationSettings = undefined, isEmailPrimary = undefined) {
    Logger.info('Started Update User Contact Info call.')
    return tmxApiCall('post', '/updateUser', {
      newAlias,
      isVoice,
      communicationSettings,
      isEmailPrimary
    })
  },

  /**
   * Verifies the user update request using the passed in verification code.
   * @param {String} newAlias - The new phone or email being verified.
   * @param {String} code - The unique confirmation code sent to the user to be confirmed.
   * @param {Boolean} [isEmailPrimary] - Is the email the primary contact?
   */
  async confirmUserUpdateCode (newAlias, code, isEmailPrimary) {
    Logger.info('Started Confirm User Update Code call.')
    return tmxApiCall('put', '/updateUser', {
      newAlias,
      verificationCode: code,
      isEmailPrimary
    })
  },

  /**
   * Updates the Cognito profile with the latest MDM identifier if found.
   * @returns {Promise<{mdmId}>} The customer's MDM identifier if found.
   */
  async updateMdmProfile () {
    Logger.info('Started update MDM Profile call.')
    return tmxApiCall('post', '/mdmProfile')
  },

  async registerNcrUser (firstName, lastName, cellNumber, email, fromLegacyNcr) {
    Logger.info('Started profile creation.')

    return tmxApiCall('post', '/registerNcrUser', {
      email,
      firstName,
      lastName,
      cellNumber,
      fromLegacyNcr
    })
  },

  /**
   * Gets the password policy for the UserPool.
   * @return {Promise<{minimumLength: Number, requireLowercase: Boolean, requireUppercase: Boolean, requireNumbers: Boolean, requireSymbols: Boolean, requireNoSpaces: Boolean}>} An object containing the password policy.
   */
  async getPasswordPolicy () {
    Logger.info('Started Get Password Policy query.')
    return tmxApiCall('get', '/passwordPolicy')
  },

  /**
   * Unlocks a users profile in the lower environment.
   * @param {String} profileId - The cognito profile of the user.
   * @return {Promise<{success: Boolean}>} Success boolean.
   */
  async unlockProfile (profileId) {
    Logger.info('Started unlock profile.')
    return tmxApiCall('post', '/profileLock', {
      profileId
    })
  },

  /**
   * Gets matching customers to simulate in the lower environment.
   * @param {String} email - The email address of the user in the LMS.
   * @return {Promise<{id: String, name: String, dob: String, state: String}[]>} An array of customer data objects.
   */
  async getSimulationCustomers (email) {
    Logger.info('Started Get customers to simulate.')
    return tmxApiCall('get', '/simulateCustomer', {
      params: {
        email
      }
    })
  },

  /**
   * Attempts to simulate a customer in the lower environment.
   * @param {String} email - The email address of the user in the LMS.
   * @param {String} tlxeCustomerId - The identifier of the user in the LMS.
   * @return {Promise<{accessToken: String, expiresIn: Number, idToken: String, refreshToken: String, tokenType: String, deviceKey: String, deviceGroupKey: String, serviceConfig: {clientId: String, cognitoUrl: String, userPoolId: String, region: String}}>} An auth result to sign in as the customer.
   */
  async simulateCustomer (email, tlxeCustomerId) {
    Logger.info('Started POST simulate customer.')
    return tmxApiCall('post', '/simulateCustomer', {
      email,
      tlxeCustomerId
    })
  },

  /**
   * Determine if a user exists based on email and/or phone number.
   * @param {string} email - The profile email address.
   * @param {string} phone - The user's phone number.
   * @param {boolean} includeAll - If true, includes unverified users.
   * @returns {Promise<{userIsConfirmed: boolean}>} True if the profile exists based on verified status and includeAll option.
   */
  async getLoginStatus (email, phone, includeAll) {
    Logger.info('Determine if a user exists based on email and/or phone number')

    return tmxApiCall('post', '/loginStatus', {
      email,
      phone,
      includeAll
    })
  },

  /**
   * Initiate validation of user phone number using Prove Service.
   * @param {string} authType - Mobile or Instant indicator.
   * @param {string} phoneNumber - The user's phone number.
   * @param {string} sessionId - The client side session ID to track all calls in the flow.
   * @returns {Promise<{sent: Boolean, url: String}>} SMS sent details for instant link or URL details for mobile auth.
   */
  async initiatePhoneVerification (authType, phoneNumber, sessionId) {
    Logger.info('Starting step to validate user phone number using Prove Service (Mobile Auth and Instant Link)')

    return tmxApiCall('post', '/phoneVerification', {
      authType,
      phoneNumber,
      sessionId
    })
  },

  /**
   * Verify user phone number validation using Prove Service.
   * @param {string} authType - Mobile or Desktop indicator.
   * @param {string} phoneNumber - The user's phone number.
   * @param {string} sessionId - The client side session ID to track all calls in the flow.
   */
  async mobileAuthConfirmation (authType, phoneNumber, sessionId, verifyKey) {
    Logger.info('Second confirmation step to confirm Phone Number through Prove (MobileAuth only)')

    return tmxApiCall('put', '/phoneVerification', {
      authType,
      phoneNumber,
      sessionId,
      verifyKey
    })
  },

  /**
   * Poll user phone number verification using Prove Service.
   * @param {string} phoneNumber - The user's phone number.
   * @returns {Promise<{success: Boolean, mobileAuthError: Boolean, skipped: Boolean}>} Whether the phone has been verified yet or not.
   */
  async getInstantLinkStatus (phoneNumber) {
    Logger.info('Polls user phone number to check Mobile verification status (Instant Link only)')

    return tmxApiCall('get', '/phoneVerification', {
      params: {
        phoneNumber
      }
    })
  },

  /**
   * Gets the verification result after user has clicked the link and saves status.
   * @param {'instant'|'mobile'} authType - Whether to do mobile auth (cellular network only - T-Mobile or Verizon) or instant link (all of devices/carriers).
   * @param {String} vfp - The phone verification key to get back the verification results.
   * @param {String} data - Optionally the AWS request ID which appears in CloudWatch to use for logging purposes in phone verify integration.
   * @param {String} sessionId - The client side session ID to track all calls in the flow.
   */
  async phoneVerifyResult (authType, vfp, data, sessionId) {
    Logger.info('Validate user phone number using Prove Service')

    return tmxApiCall('get', '/phoneVerifyResult', {
      params: {
        authType,
        vfp,
        data,
        sessionId
      }
    })
  }
}

const getUserPool = (servicesConfig = store.state.session.servicesConfig) => {
  return new CognitoUserPool({
    UserPoolId: servicesConfig.userPoolId, // Your user pool id here
    ClientId: servicesConfig.clientId, // Your client id here
    Storage: window.sessionStorage
  })
}

const getEncodedData = (username) => {
  try {
    return window.AmazonCognitoAdvancedSecurityData.getData(username, process.env.VUE_APP_CognitoUserPoolId, process.env.VUE_APP_CognitoClientId)
  } catch (e) {
    Logger.error('Failed to get encoded cognito data')
    Logger.debug(e)
  }
}
